/**
 * REQ-104: venue filter state and matching, shared by the Tour Planner map, the filter drawer,
 * the Venue Criteria rail and the Venue List. No React, no Firestore.
 *
 * The state is a superset of the Tour Planner's old `filters` object, so existing setFilters
 * callers keep working:
 *   { affiliate[], features[], resources[], capacityMin, capacityMax, showUnknownCapacity,
 *     video[], fanAccess[], artistAccess[], subscriptionFeatures[] }
 *
 * The feature matchers moved here word for word from TourPlanner.applyFilters and VenueList.applyFilters.
 * The two copies had drifted in two places, kept as named profiles so neither screen changes behavior
 * (scripts/test-venue-filters.cjs locks both in):
 *   map  (default): affiliate = AND over substring matches ("Other" = the catch-all color bucket);
 *                   Parking = "yes", or any text that doesn't say "no".
 *   list:           affiliate = OR over the comma-separated affiliate tokens; Parking = text says "yes".
 */
import { venueHasVeganFood } from './venueFeatureUtils';
import {
  FAN_ACCESS_ITEMS,
  ARTIST_ACCESS_ITEMS,
  ACCESS_YES,
  venueAccessValue,
} from '../constants/venueAccessibility';

export const VENUE_FILTER_SCHEMA_VERSION = 1;

const ARRAY_KEYS = ['affiliate', 'features', 'resources', 'video', 'fanAccess', 'artistAccess', 'subscriptionFeatures'];

export const VIDEO_CAPABILITY_OPTIONS = [
  { value: 'projector', label: 'Projector / screen' },
  { value: 'livestream', label: 'Livestream' },
  { value: 'recording', label: 'Recording / cameras' },
];

/** Same chip set as the Venue Criteria rail (MapSideRail AFFILIATE_CHIP_VALUES). */
export const VENUE_TYPE_OPTIONS = [
  'Live Nation', 'AEG', 'Independent', 'Bar', 'Brewery', 'DIY', 'Event Space',
  'Nonprofit', 'Record Shop', 'Restaurant', 'Skate', 'Art Gallery', 'Coffee Shop',
  'Tea House', 'Other',
].map((value) => ({ value, label: value }));

/** Same chip set as the rail's FEATURE_CHIP_OPTIONS (Video lives in its own group in the drawer). */
export const VENUE_FEATURE_OPTIONS = [
  { value: 'hasBackline', label: 'Backline' },
  { value: 'hasBackstage', label: 'Backstage' },
  { value: 'hasParking', label: 'Parking' },
  { value: 'hasShorePower', label: 'Shore power' },
  { value: 'hasLodging', label: 'Lodging on-site' },
  { value: 'hasRehearsal', label: 'Rehearsal space' },
  { value: 'hasVeganFood', label: 'Vegan food' },
  { value: 'hasSpecLink', label: 'Tech spec links' },
  { value: 'merch100', label: 'Merch: 100% to artist' },
  { value: 'stageFloor', label: 'Floor stage' },
  { value: 'hasLaundry', label: 'Laundry' },
  { value: 'hasShower', label: 'Shower' },
  { value: 'hasPinball', label: 'Pinball!' },
  { value: 'hasADA', label: 'Artist accessibility notes' },
];

export const ACCESS_DATA_NOTE = "Most venues don't have this yet. Help by adding a note (cone icon).";

/** UI schema for the drawer. `parent` is a single "any" row that lives in another group. */
export const VENUE_FILTER_GROUPS = [
  { id: 'affiliate', label: 'Venue type', kind: 'multi', options: VENUE_TYPE_OPTIONS },
  { id: 'capacity', label: 'Capacity', kind: 'range' },
  { id: 'features', label: 'Features', kind: 'multi', options: VENUE_FEATURE_OPTIONS },
  {
    id: 'video',
    label: 'Video',
    kind: 'multi',
    parent: { group: 'features', value: 'hasVideo', label: 'Video capable (any)' },
    options: VIDEO_CAPABILITY_OPTIONS,
  },
  {
    id: 'fanAccess',
    label: 'Accessibility: for fans',
    kind: 'multi',
    options: FAN_ACCESS_ITEMS.map(({ id, label }) => ({ value: id, label })),
    note: ACCESS_DATA_NOTE,
  },
  {
    id: 'artistAccess',
    label: 'Accessibility: for artists',
    kind: 'multi',
    options: ARTIST_ACCESS_ITEMS.map(({ id, label }) => ({ value: id, label })),
    note: ACCESS_DATA_NOTE,
  },
];

export function emptyVenueFilters() {
  return {
    affiliate: [],
    features: [],
    resources: [],
    capacityMin: '',
    capacityMax: '',
    showUnknownCapacity: false,
    video: [],
    fanAccess: [],
    artistAccess: [],
    subscriptionFeatures: [],
  };
}

/** Fill in missing keys without dropping extra ones (TourPlanner still carries venueType / hasParking). */
export function withFilterDefaults(state) {
  const base = emptyVenueFilters();
  const src = state && typeof state === 'object' ? state : {};
  const out = { ...src };
  Object.keys(base).forEach((key) => {
    if (ARRAY_KEYS.includes(key)) out[key] = Array.isArray(src[key]) ? src[key] : [];
    else if (src[key] === undefined || src[key] === null) out[key] = base[key];
  });
  return out;
}

/** Add or remove one value in an array group. Never mutates its input. */
export function toggleFilterValue(state, groupId, value) {
  const current = Array.isArray(state?.[groupId]) ? state[groupId] : [];
  const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
  return { ...state, [groupId]: next };
}

/** Clear one group, a list of groups, or (no argument) everything the module knows about. */
export function clearVenueFilters(state, groupIds) {
  const base = emptyVenueFilters();
  const ids = groupIds == null ? Object.keys(base) : [].concat(groupIds);
  const out = { ...state };
  ids.forEach((id) => {
    if (id === 'capacity') {
      out.capacityMin = '';
      out.capacityMax = '';
      out.showUnknownCapacity = false;
    } else if (id in base) {
      out[id] = Array.isArray(base[id]) ? [] : base[id];
    }
  });
  return out;
}

function hasCapacityRange(state) {
  return Boolean(state?.capacityMin) || Boolean(state?.capacityMax);
}

export function countActiveFilters(state) {
  if (!state) return 0;
  const arrays = ARRAY_KEYS.reduce((sum, key) => sum + (Array.isArray(state[key]) ? state[key].length : 0), 0);
  return arrays + (hasCapacityRange(state) ? 1 : 0);
}

/** How many picks a drawer group has (capacity counts as one). */
export function countGroupFilters(state, group) {
  if (!state || !group) return 0;
  if (group.kind === 'range') return hasCapacityRange(state) ? 1 : 0;
  // Count only this group's own options (Video capable lives in `features` but shows under Video).
  const picked = Array.isArray(state[group.id]) ? state[group.id] : [];
  const own = Array.isArray(group.options)
    ? picked.filter((v) => group.options.some((o) => o.value === v)).length
    : picked.length;
  const parent = group.parent && state[group.parent.group]?.includes(group.parent.value) ? 1 : 0;
  return own + parent;
}

// ---------- Video ----------

const NO_VIDEO_RE = /^(?:no|none|n\/a|na|-+|tbd|unknown|nope)\.?$/i;
// "No video", "None available", "no projector": a leading no / none means no gear.
const NO_VIDEO_PREFIX_RE = /^(?:no|none|n\/a)\b/i;

/** The venue lists real video gear in its Video tech-spec row. */
export function hasVideo(venue) {
  if (Array.isArray(venue?.videoCapabilities) && venue.videoCapabilities.length > 0) return true;
  const text = String(venue?.video || '').trim();
  return text.length > 0 && !NO_VIDEO_RE.test(text) && !NO_VIDEO_PREFIX_RE.test(text);
}

/** ['projector' | 'livestream' | 'recording'], structured field first, then a keyword pass over `video`. */
export function videoCapabilities(venue) {
  if (Array.isArray(venue?.videoCapabilities) && venue.videoCapabilities.length > 0) {
    return VIDEO_CAPABILITY_OPTIONS.map((o) => o.value).filter((v) => venue.videoCapabilities.includes(v));
  }
  if (!hasVideo(venue)) return [];
  const text = String(venue.video).toLowerCase();
  const out = [];
  if (/project|screen|\bled\b|led wall|lumen|\btv\b|monitor/.test(text)) out.push('projector');
  if (/stream/.test(text)) out.push('livestream');
  if (/camera|\bcam\b|record|\bsdi\b|ptz|switcher/.test(text)) out.push('recording');
  return out;
}

// ---------- Matchers ----------

function lower(value) {
  return value?.toLowerCase();
}

/** Feature matchers, moved from TourPlanner / VenueList. Only hasParking differs by profile. */
export function venueHasFeature(venue, feature, profile = 'map') {
  switch (feature) {
    case 'hasBackline':
      return venue.backline?.toLowerCase().includes('yes') || (venue.backline?.length > 0 && !venue.backline?.toLowerCase().includes('no'));
    case 'hasBackstage':
      return venue.backstage && venue.backstage.trim() !== '';
    case 'hasLaundry':
      return venue.backstage?.toLowerCase().includes('laundry');
    case 'hasLodging':
      return venue.backstage?.toLowerCase().includes('lodging') || venue.backstage?.toLowerCase().includes('hotel') || venue.backstage?.toLowerCase().includes('bed');
    case 'hasParking':
      if (profile === 'list') return venue.parking?.toLowerCase().includes('yes');
      return venue.parking?.toLowerCase().includes('yes') || (venue.parking?.length > 0 && !venue.parking?.toLowerCase().includes('no'));
    case 'hasShower':
      return venue.backstage?.toLowerCase().includes('shower');
    case 'hasShorePower':
      return venue.shorePower?.toLowerCase().includes('yes') || (venue.shorePower?.length > 0 && !venue.shorePower?.toLowerCase().includes('no'));
    case 'hasVeganFood':
      return venueHasVeganFood(venue);
    case 'hasPinball':
      return venue.backstage?.toLowerCase().includes('pinball') || venue.notes?.toLowerCase().includes('pinball');
    case 'merch100':
      return venue.merch?.toLowerCase().includes('100%');
    case 'stageFloor':
      return venue.stage?.toLowerCase().includes('floor');
    case 'hasRehearsal':
      return venue.backstage?.toLowerCase().includes('rehearsal') || venue.notes?.toLowerCase().includes('rehearsal');
    case 'hasSpecLink':
      return (venue.specSheets && venue.specSheets.length > 0) || (venue.specSheetLink && (venue.specSheetLink?.startsWith('http://') || venue.specSheetLink?.startsWith('https://')));
    case 'hasADA':
      return venue.adaAccessibilityArtist?.length > 0;
    case 'hasVideo':
      return hasVideo(venue);
    case 'hasIndoor':
      // no spaceType field — check stage, notes, and backstage text
      return (
        lower(venue.stage)?.includes('indoor') ||
        lower(venue.notes)?.includes('indoor') ||
        lower(venue.backstage)?.includes('indoor')
      );
    case 'hasOutdoorFloor':
      return (
        lower(venue.stage)?.includes('outdoor') ||
        lower(venue.notes)?.includes('outdoor') ||
        lower(venue.backstage)?.includes('outdoor')
      );
    default:
      return true;
  }
}

function venueMatchesAffiliate(venue, affiliate, ctx) {
  if (ctx.profile === 'list') {
    const af = String(affiliate || '').trim().toLowerCase();
    if (!af) return false;
    const tokens = String(venue.affiliate || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);
    if (af === 'skate') {
      return venue.notes?.toLowerCase().includes('skate') || venue.name?.toLowerCase().includes('skate') || tokens.includes('skate');
    }
    return tokens.includes(af);
  }
  if (affiliate === 'Skate') {
    return venue.notes?.toLowerCase().includes('skate') || venue.name?.toLowerCase().includes('skate');
  }
  // "Other" = the catch-all blue bucket: anything not Live Nation / AEG / Independent.
  if (affiliate === 'Other' && typeof ctx.classifyAffiliateBucket === 'function') {
    return ctx.classifyAffiliateBucket(venue.affiliate) === 'other';
  }
  return venue.affiliate?.toLowerCase().includes(affiliate.toLowerCase());
}

function venueMatchesCapacity(venue, state) {
  let capacity = 0;
  if (venue.capacity) {
    const capacityStr = String(venue.capacity).replace(/,/g, '');
    capacity = parseInt(capacityStr, 10) || 0;
  }
  const hasCapacity = venue.capacity &&
    (typeof venue.capacity === 'string' ? venue.capacity.trim() !== '' : venue.capacity !== null && venue.capacity !== undefined);
  if (!hasCapacity) return Boolean(state.showUnknownCapacity);
  const minCapacity = state.capacityMin ? parseInt(state.capacityMin, 10) : null;
  const maxCapacity = state.capacityMax ? parseInt(state.capacityMax, 10) : null;
  if (minCapacity && capacity < minCapacity) return false;
  if (maxCapacity && capacity > maxCapacity) return false;
  return true;
}

function venueMatchesSubscriptionFeature(venue, feature) {
  switch (feature) {
    case 'showNotes':
      return venue.notes && venue.notes.trim() !== '';
    case 'hasSpecLink':
      return venueHasFeature(venue, 'hasSpecLink');
    default:
      return true;
  }
}

/**
 * ctx: {
 *   profile: 'map' | 'list',
 *   classifyAffiliateBucket(affiliate) -> bucket id   (map profile "Other"),
 *   resourceMatcher(venue, categoryId) -> bool         (resources group; skipped when absent),
 *   accessLookup(venue, side, id) -> 'yes'|'no'|'unknown'  (defaults to venueAccessValue),
 * }
 * Every group is AND: a venue must match every pick. Only an accessibility "yes" matches.
 */
export function venueMatchesFilters(venue, state, ctx = {}) {
  if (!venue) return false;
  const s = state || {};
  const profile = ctx.profile === 'list' ? 'list' : 'map';
  const c = { ...ctx, profile };

  if (s.affiliate?.length > 0) {
    const test = (affiliate) => venueMatchesAffiliate(venue, affiliate, c);
    if (!(profile === 'list' ? s.affiliate.some(test) : s.affiliate.every(test))) return false;
  }
  if (s.features?.length > 0 && !s.features.every((f) => venueHasFeature(venue, f, profile))) return false;
  if (s.resources?.length > 0 && typeof c.resourceMatcher === 'function'
    && !s.resources.every((res) => c.resourceMatcher(venue, res))) return false;
  if (hasCapacityRange(s) && !venueMatchesCapacity(venue, s)) return false;
  if (s.subscriptionFeatures?.length > 0 && !s.subscriptionFeatures.every((f) => venueMatchesSubscriptionFeature(venue, f))) return false;
  if (s.video?.length > 0) {
    const caps = videoCapabilities(venue);
    if (!s.video.every((v) => (v === 'any' ? hasVideo(venue) : caps.includes(v)))) return false;
  }
  const lookup = typeof c.accessLookup === 'function' ? c.accessLookup : venueAccessValue;
  if (s.fanAccess?.length > 0 && !s.fanAccess.every((id) => lookup(venue, 'fan', id) === ACCESS_YES)) return false;
  if (s.artistAccess?.length > 0 && !s.artistAccess.every((id) => lookup(venue, 'artist', id) === ACCESS_YES)) return false;
  return true;
}

/** Keeps input order. Returns a new array. */
export function applyVenueFilters(venues, state, ctx = {}) {
  const list = Array.isArray(venues) ? venues : [];
  if (countActiveFilters(state) === 0) return [...list];
  return list.filter((venue) => venueMatchesFilters(venue, state, ctx));
}

// ---------- Persistence ----------

/** Compact, versioned JSON for calendarV2Prefs / URLs. Only non-empty keys are written. */
export function serializeVenueFilters(state) {
  const s = withFilterDefaults(state);
  const out = { v: VENUE_FILTER_SCHEMA_VERSION };
  ARRAY_KEYS.forEach((key) => {
    if (s[key].length > 0) out[key] = [...s[key]];
  });
  if (s.capacityMin) out.capacityMin = String(s.capacityMin);
  if (s.capacityMax) out.capacityMax = String(s.capacityMax);
  if (s.showUnknownCapacity) out.showUnknownCapacity = true;
  return JSON.stringify(out);
}

/** Tolerant: accepts JSON or an object, drops unknown keys and junk values, upgrades old shapes. */
export function parseVenueFilters(raw) {
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return emptyVenueFilters();
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return emptyVenueFilters();
  const out = emptyVenueFilters();
  ARRAY_KEYS.forEach((key) => {
    if (Array.isArray(obj[key])) {
      out[key] = [...new Set(obj[key].filter((v) => typeof v === 'string' && v.trim()))];
    }
  });
  ['capacityMin', 'capacityMax'].forEach((key) => {
    const v = obj[key];
    if ((typeof v === 'string' || typeof v === 'number') && /^\d+$/.test(String(v).trim())) out[key] = String(v).trim();
  });
  out.showUnknownCapacity = obj.showUnknownCapacity === true;
  return out;
}
